import { ABBREVIATED_PACK_UNITS, LOCALIZED_PACK_UNITS } from './i18n';
import { AvailabilityStatusConfig } from '@/contexts/dynamic-config';
import { EmployeeRole } from '@/types';
import { ItemListOption, ItemSortOrder } from '@/contexts/item-context-types';
import { PackUnit, VariantPackAvailabilityStatus } from '@/enums';

/**
 * Returns the localized display string for a pack unit.
 * Falls back to the raw pack unit value if no localization is found.
 *
 * @param packUnit - The pack unit enum value to localize
 * @returns The localized pack unit string, or the raw value as fallback
 */
export const getLocalizedPackUnit = (packUnit: PackUnit): string => {
  return (LOCALIZED_PACK_UNITS[packUnit] || packUnit).toLowerCase();
};

/**
 * Returns the abbreviated display string for a pack unit (e.g. "lb", "oz", "kg").
 * Falls back to the full localized name if no abbreviation is defined.
 */
export const getAbbreviatedPackUnit = (packUnit: PackUnit): string => {
  return (ABBREVIATED_PACK_UNITS[packUnit] || packUnit).toLowerCase();
};

interface PackOptions {
  abbreviated?: boolean;
  maxUnitSize: number | string | null;
  minUnitSize: number | string | null;
  unit: PackUnit | null;
}

/**
 * Formats a pack unit with its size into a human-readable display string.
 *
 * Combines the unit size range with the localized (or abbreviated) unit name.
 * When the size is 1, only the unit name is returned. For ranges, both min
 * and max sizes are included with an en-dash separator.
 *
 * @param options.unit - The pack unit enum value (e.g. PackUnit.Pound)
 * @param options.minUnitSize - Minimum unit size (defaults to 1 if falsy)
 * @param options.maxUnitSize - Maximum unit size (defaults to 1 if falsy)
 * @param options.abbreviated - When true, uses abbreviated unit names (e.g. "lb" instead of "pound")
 * @returns Formatted display string (e.g. "pound", "12 lb", "1 – 2 kg"), or empty string if no unit
 *
 * @example
 * getPackDisplayString({ unit: PackUnit.Pound, minUnitSize: 1, maxUnitSize: 1 })
 * // "pound"
 * getPackDisplayString({ unit: PackUnit.Pound, minUnitSize: 12, maxUnitSize: 12, abbreviated: true })
 * // "12 lb"
 */
export const getPackDisplayString = (options: PackOptions) => {
  if (!options.unit) {
    return '';
  }
  const maxUnitSize = Number(options.maxUnitSize) || 1;
  const minUnitSize = Number(options.minUnitSize) || 1;
  const unit = options.unit;

  const sizeString =
    minUnitSize === maxUnitSize
      ? minUnitSize
      : `${minUnitSize} – ${maxUnitSize}`;
  const unitString = options.abbreviated
    ? getAbbreviatedPackUnit(unit)
    : getLocalizedPackUnit(unit);

  return (sizeString === 1 ? unitString : `${sizeString} ${unitString}`).trim();
};

/**
 * Checks if an item is out of stock based on its available quantity.
 * @param {number | null} availableQuantity - The available quantity of the item.
 * @returns {boolean} True if the item is out of stock (quantity <= 0), false otherwise.
 */
export const isItemOutOfStock = (availableQuantity: number | null): boolean => {
  return typeof availableQuantity === 'number' && availableQuantity <= 0;
};

interface AddToOrderDisabledParams {
  availabilityStatus: VariantPackAvailabilityStatus | null;
  availabilityStatusConfig?: AvailabilityStatusConfig;
  availableQuantity: number | null;
  supplierEmployeeRole: EmployeeRole | null;
}

/**
 * Determines if an item can be added to an order based on multiple criteria:
 *
 * 1. With `availabilityStatusEnabled` set to false:
 *    - Only checks if available_quantity is 0
 *
 * 2. With `availabilityStatusEnabled` set to true:
 *    - If no status exists: checks if available_quantity is 0
 *    - If status exists: Validates based on user role
 *      (distributor employee roles or operator employee) and item's availability status
 *
 * @param {AddToOrderDisabledParams} params - Input parameters for validation
 * @returns {boolean} - true if item cannot be added, false if it can be added
 */
export const isAddToOrderDisabled = ({
  availableQuantity,
  availabilityStatus,
  availabilityStatusConfig,
  supplierEmployeeRole,
}: AddToOrderDisabledParams): boolean => {
  if (
    !availabilityStatusConfig ||
    !availabilityStatusConfig?.enabled ||
    !availabilityStatus
  ) {
    // Either availability status is enabled and there is no availability status
    // or availability status is not enabled. Fall back to available quantity
    return isItemOutOfStock(availableQuantity);
  }

  // Availability status overrides the quantity check, determine if the item is
  // orderable based on the users role and the allowed statuses for that role
  const allowedStatuses = supplierEmployeeRole
    ? (availabilityStatusConfig.enabled_statuses_for_supplier_employees?.[
        supplierEmployeeRole
      ] ?? [])
    : (availabilityStatusConfig.enabled_statuses_for_operator_employees ?? []);

  return !allowedStatuses.includes(availabilityStatus);
};

const compare = (a: string, b: string) => a.localeCompare(b);
const compareNullsLast = (a?: string | null, b?: string | null) => {
  if (a === b) {
    return 0;
  }
  if (!a) {
    return 1;
  }
  if (!b) {
    return -1;
  }
  return a.localeCompare(b);
};

export const sortItemListOptions = (
  itemListOptions: ItemListOption[],
  sortOrder: ItemSortOrder,
) => {
  switch (sortOrder) {
    case ItemSortOrder.Alphabetical:
      itemListOptions.sort(
        (a, b) =>
          compare(a.title, b.title) ||
          compare(a.metadata!.packUnit, b.metadata!.packUnit) ||
          compareNullsLast(a.metadata!.externalID, b.metadata!.externalID),
      );
      break;
    case ItemSortOrder.AlphabeticalReverse:
      itemListOptions.sort(
        (a, b) =>
          compare(b.title, a.title) ||
          compare(b.metadata!.packUnit, a.metadata!.packUnit) ||
          compareNullsLast(b.metadata!.externalID, a.metadata!.externalID),
      );
      break;
    case ItemSortOrder.ItemCode:
      itemListOptions.sort(
        (a, b) =>
          compareNullsLast(a.metadata!.externalID, b.metadata!.externalID) ||
          compare(a.title, b.title) ||
          compare(a.metadata!.packUnit, b.metadata!.packUnit),
      );
      break;
    case ItemSortOrder.ItemCodeReverse:
      itemListOptions.sort(
        (a, b) =>
          compareNullsLast(b.metadata!.externalID, a.metadata!.externalID) ||
          compare(b.title, a.title) ||
          compare(b.metadata!.packUnit, a.metadata!.packUnit),
      );
      break;
    case ItemSortOrder.PurchasedDate:
      itemListOptions.sort(
        (a, b) =>
          compare(b.metadata!.orderDate || '', a.metadata!.orderDate || '') ||
          compare(a.title, b.title) ||
          compare(a.metadata!.packUnit, b.metadata!.packUnit),
      );
      break;
  }

  return itemListOptions;
};
